import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseCategoriesAPI } from '../services/api';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import StaffPageShell from '../components/StaffPageShell';

const CourseCategoryManagementPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSectionsModal, setShowSectionsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    categoryName: '',
    categoryCode: '',
    categoryType: 'PO',
    description: '',
    isActive: true
  });

  // Section types
  const sectionTypes = [
    { value: 'PersonalAcademic', label: 'Personal, Academic & VE Details' },
    { value: 'MockInterview', label: 'Mock Interview' },
    { value: 'InterviewReadiness', label: 'Interview Readiness' },
    { value: 'TrainingConcerns', label: 'Training Concerns' },
    { value: 'CounsellingDetails', label: 'Counselling Details' },
    { value: 'PlacementOpportunity', label: 'Placement Opportunity' },
    { value: 'PlacementDone', label: 'Placement Done' },
    { value: 'Testimony', label: 'Testimony' }
  ];

  const categoryTypes = [
    { value: 'PO', label: 'Placement Oriented (PO)' },
    { value: 'SLP', label: 'Systematic Learning Program (SLP)' },
    { value: 'JOU', label: 'Job Oriented Upskilling (JOU)' },
    { value: 'TI', label: 'Training cum Internship (TI)' },
    { value: 'UP', label: 'Upskilling Program (UP)' },
    { value: 'WEBINAR', label: 'Webinar' },
    { value: 'WS', label: 'Workshop (WS)' }
  ];

  const [selectedSections, setSelectedSections] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const response = await execute(async () => await courseCategoriesAPI.getAll());
    if (response?.data !== undefined) {
      setCategories(normalizeApiListResponse(response.data));
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setFormData({
      categoryName: '',
      categoryCode: '',
      categoryType: 'PO',
      description: '',
      isActive: true
    });
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      categoryName: category.categoryName || '',
      categoryCode: category.categoryCode || '',
      categoryType: category.categoryType || category.categoryCode || 'PO',
      description: category.description || '',
      isActive: category.isActive !== undefined ? category.isActive : true
    });
    setShowModal(true);
  };

  // Auto-sync categoryName, categoryCode, and categoryType
  const handleFieldChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Map category names to codes and types
    const nameToCodeMap = {
      'Placement Oriented': { code: 'PO', type: 'PO' },
      'Systematic Learning Program': { code: 'SLP', type: 'SLP' },
      'Job Oriented Upskilling': { code: 'JOU', type: 'JOU' },
      'Training cum Internship': { code: 'TI', type: 'TI' },
      'Upskilling Program': { code: 'UP', type: 'UP' },
      'Webinar': { code: 'WEBINAR', type: 'WEBINAR' },
      'Workshop': { code: 'WS', type: 'WS' }
    };
    
    if (field === 'categoryName' && value) {
      const normalizedName = value.trim();
      const mapping = nameToCodeMap[normalizedName];
      if (mapping) {
        newFormData.categoryCode = mapping.code;
        newFormData.categoryType = mapping.type;
      }
    } else if (field === 'categoryCode' && value) {
      // When code changes, update type to match
      const code = value.toUpperCase();
      const codeToTypeMap = {
        'PO': 'PO',
        'SLP': 'SLP',
        'JOU': 'JOU',
        'TI': 'TI',
        'UP': 'UP',
        'WEBINAR': 'WEBINAR',
        'WS': 'WS'
      };
      newFormData.categoryType = codeToTypeMap[code] || code;
    } else if (field === 'categoryType' && value) {
      // When type changes, update code to match
      newFormData.categoryCode = value.toUpperCase();
    }
    
    setFormData(newFormData);
  };

  const handleManageSections = async (category) => {
    setSelectedCategory(category);
    try {
      const response = await courseCategoriesAPI.getSections(category.id);
      const sections = response.data?.data || category.availableSections || [];
      setSelectedSections(sections);
      setShowSectionsModal(true);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setSelectedSections(category.availableSections || []);
      setShowSectionsModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Auto-sync categoryName, categoryCode, and categoryType
    const submitData = { ...formData };
    
    // Map category names to codes and types
    const nameToCodeMap = {
      'Placement Oriented': { code: 'PO', type: 'PO' },
      'Systematic Learning Program': { code: 'SLP', type: 'SLP' },
      'Job Oriented Upskilling': { code: 'JOU', type: 'JOU' },
      'Training cum Internship': { code: 'TI', type: 'TI' },
      'Upskilling Program': { code: 'UP', type: 'UP' },
      'Webinar': { code: 'WEBINAR', type: 'WEBINAR' },
      'Workshop': { code: 'WS', type: 'WS' }
    };
    
    // If categoryName is set, auto-set categoryCode and categoryType
    if (submitData.categoryName) {
      const normalizedName = submitData.categoryName.trim();
      const mapping = nameToCodeMap[normalizedName];
      
      if (mapping) {
        // Auto-set code and type from name
        submitData.categoryCode = mapping.code;
        submitData.categoryType = mapping.type;
      } else if (!submitData.categoryCode && !submitData.categoryType) {
        // If name doesn't match known patterns, try to extract code from name
        // or use the name as a fallback
        const words = normalizedName.split(' ');
        if (words.length > 0) {
          // Try to find acronym in name (e.g., "Placement Oriented (PO)")
          const acronymMatch = normalizedName.match(/\(([A-Z]+)\)/);
          if (acronymMatch) {
            submitData.categoryCode = acronymMatch[1];
            submitData.categoryType = acronymMatch[1];
          } else {
            // Use first letters of words as code
            const code = words.map(w => w[0]?.toUpperCase()).join('').substring(0, 10);
            submitData.categoryCode = code;
            submitData.categoryType = code;
          }
        }
      }
    }
    
    // If categoryCode is set but categoryType is not, set categoryType to match code
    if (submitData.categoryCode && !submitData.categoryType) {
      const codeToTypeMap = {
        'PO': 'PO',
        'SLP': 'SLP',
        'JOU': 'JOU',
        'TI': 'TI',
        'UP': 'UP',
        'WEBINAR': 'WEBINAR',
        'WS': 'WS'
      };
      submitData.categoryType = codeToTypeMap[submitData.categoryCode.toUpperCase()] || submitData.categoryCode.toUpperCase();
    }
    
    // If categoryType is set but categoryCode is not, set categoryCode to match type
    if (submitData.categoryType && !submitData.categoryCode) {
      submitData.categoryCode = submitData.categoryType;
    }
    
    // Ensure categoryType and categoryCode always match
    if (submitData.categoryCode && submitData.categoryType) {
      if (submitData.categoryCode.toUpperCase() !== submitData.categoryType.toUpperCase()) {
        // If they don't match, prioritize categoryCode
        submitData.categoryType = submitData.categoryCode.toUpperCase();
      }
    }
    
    const response = await execute(async () => {
      if (editingCategory) {
        return await courseCategoriesAPI.update(editingCategory.id, submitData);
      } else {
        return await courseCategoriesAPI.create(submitData);
      }
    }, { 
      successMessage: `Course category ${editingCategory ? 'updated' : 'created'} successfully!` 
    });
    
    if (response) {
      setShowModal(false);
      setEditingCategory(null);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleSectionsSubmit = async (e) => {
    e.preventDefault();
    
    const response = await execute(async () => {
      return await courseCategoriesAPI.updateSections(selectedCategory.id, {
        sectionNames: selectedSections
      });
    }, { 
      successMessage: 'Category sections updated successfully!' 
    });
    
    if (response) {
      setShowSectionsModal(false);
      setSelectedCategory(null);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const toggleSection = (sectionValue) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionValue)) {
        return prev.filter(s => s !== sectionValue);
      } else {
        return [...prev, sectionValue];
      }
    });
  };

  return (
    <StaffPageShell title="Course Categories" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Create Category
          </button>
        </div>

        <MessageDisplay error={error} success={success} />

        {loading && <LoadingSpinner />}

        <div className="theme-card rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="p-4 border rounded-lg theme-border hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'var(--color-surface)' }}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold theme-text">{category.categoryName}</h3>
                    <p className="text-sm theme-text-secondary">Code: {category.categoryCode}</p>
                    <p className="text-xs theme-text-secondary mt-1">Type: {category.categoryType}</p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      category.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {category.description && (
                  <p className="text-sm theme-text-secondary mb-3">{category.description}</p>
                )}

                <div className="mb-3">
                  <p className="text-xs theme-text-secondary mb-1">
                    Available Sections: {category.availableSections?.length || category.sectionCount || 0}
                  </p>
                  {category.availableSections && category.availableSections.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {category.availableSections.slice(0, 3).map((section, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          {section}
                        </span>
                      ))}
                      {category.availableSections.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs">
                          +{category.availableSections.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(category)}
                    className="flex-1 px-3 py-1.5 text-sm rounded theme-button-secondary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleManageSections(category)}
                    className="flex-1 px-3 py-1.5 text-sm rounded theme-button-primary"
                  >
                    Manage Sections
                  </button>
                </div>
              </div>
            ))}
          </div>

          {categories.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="theme-text-secondary">No course categories found. Create one to get started.</p>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
          title={editingCategory ? 'Edit Course Category' : 'Create Course Category'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Category Name *
              </label>
              <input
                type="text"
                required
                value={formData.categoryName}
                onChange={(e) => handleFieldChange('categoryName', e.target.value)}
                className="w-full px-3 py-2 rounded-lg theme-input"
                placeholder="e.g., Placement Oriented"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.categoryCode && `Code will auto-set to: ${formData.categoryCode}`}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Category Code *
              </label>
              <input
                type="text"
                required
                value={formData.categoryCode}
                onChange={(e) => handleFieldChange('categoryCode', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 rounded-lg theme-input"
                placeholder="e.g., PO"
                maxLength={10}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.categoryType && `Type will auto-set to: ${formData.categoryType}`}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Category Type * (Auto-synced with Code)
              </label>
              <SearchableSelect
                required
                value={formData.categoryType}
                onChange={(e) => handleFieldChange('categoryType', e.target.value)}
                className="w-full"
                options={categoryTypes}
              />
              <p className="text-xs text-gray-500 mt-1">
                Type and Code are kept in sync automatically
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg theme-input"
                rows="3"
                placeholder="Category description..."
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm theme-text">
                Active
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
              >
                {loading ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingCategory(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>

        {/* Manage Sections Modal */}
        <Modal
          isOpen={showSectionsModal}
          onClose={() => {
            setShowSectionsModal(false);
            setSelectedCategory(null);
          }}
          title={`Manage Sections - ${selectedCategory?.categoryName || ''}`}
          size="large"
        >
          <form onSubmit={handleSectionsSubmit} className="space-y-4">
            <p className="text-sm theme-text-secondary mb-4">
              Select which sections should be available for this course category:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sectionTypes.map((section) => (
                <label
                  key={section.value}
                  className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-opacity-50 transition-colors"
                  style={{
                    backgroundColor: selectedSections.includes(section.value)
                      ? 'var(--color-primary)'
                      : 'var(--color-surface)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSections.includes(section.value)}
                    onChange={() => toggleSection(section.value)}
                    className="mr-3"
                  />
                  <span className="theme-text">{section.label}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Sections'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSectionsModal(false);
                  setSelectedCategory(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
    </StaffPageShell>
  );
};

export default CourseCategoryManagementPage;
